import { useEffect, useState, type ReactNode } from "react";
import { Icon, type IconName } from "@/components/common/Icon";
import { IconButton } from "@/components/common/IconButton";
import { Spinner } from "@/components/common/Spinner";
import { useToastStore, type Toast as ToastModel, type ToastType } from "@/stores/useToastStore";

const AUTO_DISMISS_MS = 5000;
const ENTER_TRANSITION_MS = 180;
const LEADING_ICON_SIZE = 18;
interface ToastTone {
	background: string;
	ring: string;
	accent: string;
}

const NEUTRAL_TONE: ToastTone = {
	background: "var(--surface-2)",
	ring: "var(--shadow-card)",
	accent: "var(--fg-2)",
};

const TONES: Record<ToastType, ToastTone> = {
	success: { ...NEUTRAL_TONE, accent: "var(--success)" },
	error: {
		background: "var(--danger-faint)",
		ring: "0 0 0 1px var(--danger-line)",
		accent: "var(--danger)",
	},
	info: NEUTRAL_TONE,
	loading: NEUTRAL_TONE,
};

const ICONS: Record<Exclude<ToastType, "loading">, IconName> = {
	success: "checkCircle",
	error: "alertCircle",
	info: "info",
};

// アイコンと × を本文の 1 行目の高さで中央に揃える。
function FirstLineSlot({ width, children }: { width?: number; children: ReactNode }) {
	return (
		<div
			style={{
				flex: "none",
				display: "flex",
				alignItems: "center",
				justifyContent: "center",
				width,
				height: "1lh",
			}}
		>
			{children}
		</div>
	);
}

interface ToastProps {
	toast: ToastModel;
	onDismiss: (id: string) => void;
}

function Toast({ toast, onDismiss }: ToastProps) {
	const tone = TONES[toast.type];
	const [entered, setEntered] = useState(false);

	useEffect(() => {
		const raf = requestAnimationFrame(() => setEntered(true));
		return () => cancelAnimationFrame(raf);
	}, []);

	const isLoading = toast.type === "loading";
	useEffect(() => {
		if (isLoading) return;
		const timer = window.setTimeout(() => onDismiss(toast.id), AUTO_DISMISS_MS);
		return () => window.clearTimeout(timer);
	}, [toast.id, isLoading, onDismiss]);

	return (
		<div
			data-testid={`toast-${toast.type}`}
			data-toast-id={toast.id}
			style={{
				display: "flex",
				alignItems: "flex-start",
				gap: 12,
				minWidth: 280,
				maxWidth: 420,
				// × を出すときは、ボタン内側の余白の分だけ右を詰める。
				padding: isLoading ? "14px 16px" : "14px 12px 14px 16px",
				borderRadius: "var(--radius-5)",
				background: tone.background,
				color: "var(--fg-1)",
				boxShadow: tone.ring,
				fontSize: "var(--text-ui)",
				lineHeight: "var(--leading-normal)",
				transform: entered ? "translateY(0)" : "translateY(8px)",
				opacity: entered ? 1 : 0,
				transition: `transform ${ENTER_TRANSITION_MS}ms ease-out, opacity ${ENTER_TRANSITION_MS}ms ease-out`,
			}}
		>
			{/* スピナーはアイコンより小さいので、同じ幅の枠で中央に置いて本文の書き出しを揃える。 */}
			<FirstLineSlot width={LEADING_ICON_SIZE}>
				{toast.type === "loading" ? (
					<Spinner size={16} color={tone.accent} data-testid="toast-loading-spinner" />
				) : (
					<Icon name={ICONS[toast.type]} size={LEADING_ICON_SIZE} color={tone.accent} />
				)}
			</FirstLineSlot>
			<span style={{ flex: 1, minWidth: 0, wordBreak: "break-word" }}>{toast.message}</span>
			{/* 進行中トーストは完了時に呼び出し側が消すため、手動で閉じる手段は出さない。 */}
			{!isLoading && (
				<FirstLineSlot>
					<IconButton
						data-testid="toast-dismiss"
						icon="x"
						size={20}
						iconSize={14}
						color="var(--fg-2)"
						hoverBackground="var(--overlay-hover)"
						activeBackground="var(--overlay-active)"
						onClick={() => onDismiss(toast.id)}
					/>
				</FirstLineSlot>
			)}
		</div>
	);
}

export function ToastContainer() {
	const toasts = useToastStore((s) => s.toasts);
	const dismiss = useToastStore((s) => s.dismiss);

	if (toasts.length === 0) return null;

	return (
		<div
			data-testid="toast-container"
			style={{
				position: "fixed",
				bottom: 16,
				right: 16,
				zIndex: 1000,
				display: "flex",
				flexDirection: "column",
				gap: 8,
			}}
		>
			{toasts.map((toast) => (
				<Toast key={toast.id} toast={toast} onDismiss={dismiss} />
			))}
		</div>
	);
}
